import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../api/axios'

// ── Tipos (mesmo formato de /publico/resultados e /publico/ranking-geral,
// ja usados e validados em ResultadosPublicoPage) ─────────────────────────────

interface ResultadoItem {
  posicao: number
  nome_atirador: string
  clube_nome: string
  categoria: string
  total: number | null
  qtd_x: number | null
  total_pontos: number | null
  carneiro: number | null
  peru: number | null
  porco: number | null
  galinha: number | null
}

interface EtapaResultado {
  idetapa: number
  numero_etapa: number
  titulo: string
  titulo_reduzido: string
  resultados: ResultadoItem[]
}

interface ModalidadeResultado {
  idmodalidade: number
  nome: string
  grupo: string
  etapas: EtapaResultado[]
}

interface ResultadosResponse {
  modalidades: ModalidadeResultado[]
}

interface Campeonato {
  idcampeonato: number
  descricao: string
  temporada: number
  clube_nome: string
}

interface RankingItem {
  posicao: number
  idatirador: number
  nome_atirador: string
  clube_nome: string
  etapas: Record<number, number>
  total_geral: number
}

interface RankingGeralResponse {
  grupo: string
  etapas_disponiveis: Array<{ idetapa: number; numero_etapa: number; titulo_reduzido: string; titulo: string }>
  ranking: RankingItem[]
}

// ── Slides: fase 1 = resultado da etapa atual por modalidade; fase 2 =
// classificacao geral (colunas por etapa + total) por modalidade ─────────────

interface SlideEtapa {
  tipo: 'etapa'
  key: string
  modalidadeNome: string
  grupo: string
  etapaLabel: string
  resultados: ResultadoItem[]
  pagina: number
  totalPaginas: number
}

interface SlideGeral {
  tipo: 'geral'
  key: string
  modalidadeNome: string
  grupo: string
  etapaColunas: Array<{ numero_etapa: number; label: string }>
  ranking: RankingItem[]
  pagina: number
  totalPaginas: number
}

type Slide = SlideEtapa | SlideGeral

const ROWS_PER_SLIDE = 24
const SEGUNDOS_POR_SLIDE = 8
const SEGUNDOS_ATUALIZAR_DADOS = 20

// Etapa-alvo da Fase 1: a maior numero_etapa que ja tem resultado lancado em
// QUALQUER modalidade do campeonato (nao por modalidade individualmente) —
// senao uma modalidade ainda travada na etapa 1 aparecia mostrando a etapa 1
// no meio do ciclo, mesmo com as outras ja na etapa 2.
function etapaAlvoGlobal(data: ResultadosResponse | null): number | null {
  if (!data) return null
  let maior: number | null = null
  for (const mod of data.modalidades) {
    for (const et of mod.etapas) {
      if (et.resultados.length === 0) continue
      if (maior == null || et.numero_etapa > maior) maior = et.numero_etapa
    }
  }
  return maior
}

function montarSlidesEtapa(data: ResultadosResponse | null, numeroEtapaAlvo: number | null): SlideEtapa[] {
  if (!data) return []
  const alvo = numeroEtapaAlvo ?? etapaAlvoGlobal(data)
  if (alvo == null) return []
  const slides: SlideEtapa[] = []
  for (const mod of data.modalidades) {
    const etapa = mod.etapas.find(e => e.numero_etapa === alvo)
    if (!etapa || etapa.resultados.length === 0) continue

    const totalPaginas = Math.max(1, Math.ceil(etapa.resultados.length / ROWS_PER_SLIDE))
    for (let pagina = 0; pagina < totalPaginas; pagina++) {
      slides.push({
        tipo: 'etapa',
        key: `etapa-${mod.idmodalidade}-${etapa.idetapa}-${pagina}`,
        modalidadeNome: mod.nome,
        grupo: mod.grupo,
        etapaLabel: etapa.titulo_reduzido || `${etapa.numero_etapa}a Etapa`,
        resultados: etapa.resultados.slice(pagina * ROWS_PER_SLIDE, (pagina + 1) * ROWS_PER_SLIDE),
        pagina: pagina + 1,
        totalPaginas,
      })
    }
  }
  return slides
}

// So mostra a fase "geral" quando ha pelo menos 2 etapas com resultado —
// com 1 etapa so, o combinado seria identico ao resultado da etapa (redundante).
function montarSlidesGeral(
  data: ResultadosResponse | null,
  rankingGeralMap: Record<number, RankingGeralResponse>,
): SlideGeral[] {
  if (!data) return []
  const slides: SlideGeral[] = []
  for (const mod of data.modalidades) {
    const resp = rankingGeralMap[mod.idmodalidade]
    if (!resp || resp.ranking.length === 0 || resp.etapas_disponiveis.length < 2) continue

    const etapaColunas = [...resp.etapas_disponiveis]
      .sort((a, b) => a.numero_etapa - b.numero_etapa)
      .map(e => ({ numero_etapa: e.numero_etapa, label: e.titulo_reduzido || `${e.numero_etapa}a Etapa` }))

    const totalPaginas = Math.max(1, Math.ceil(resp.ranking.length / ROWS_PER_SLIDE))
    for (let pagina = 0; pagina < totalPaginas; pagina++) {
      slides.push({
        tipo: 'geral',
        key: `geral-${mod.idmodalidade}-${pagina}`,
        modalidadeNome: mod.nome,
        grupo: mod.grupo,
        etapaColunas,
        ranking: resp.ranking.slice(pagina * ROWS_PER_SLIDE, (pagina + 1) * ROWS_PER_SLIDE),
        pagina: pagina + 1,
        totalPaginas,
      })
    }
  }
  return slides
}

function medalhaCor(posicao: number): string | null {
  if (posicao === 1) return '#F9A825'
  if (posicao === 2) return '#B0BEC5'
  if (posicao === 3) return '#BF6B3A'
  return null
}

export function TvResultadosPage() {
  const [searchParams] = useSearchParams()
  const idcampeonatoParam = searchParams.get('idcampeonato')
  const numeroEtapaParam = searchParams.get('numero_etapa')

  const [campeonatos, setCampeonatos] = useState<Campeonato[]>([])
  const [idcampeonato, setIdcampeonato] = useState<number | null>(idcampeonatoParam ? parseInt(idcampeonatoParam, 10) : null)
  const [data, setData] = useState<ResultadosResponse | null>(null)
  const [rankingGeralMap, setRankingGeralMap] = useState<Record<number, RankingGeralResponse>>({})
  const [slideIdx, setSlideIdx] = useState(0)
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const numeroEtapaAlvo = numeroEtapaParam ? parseInt(numeroEtapaParam, 10) : null

  // Carrega lista de campeonatos e escolhe o mais recente se nao veio na URL
  useEffect(() => {
    api.get<{ data: Campeonato[] }>('/publico/campeonatos')
      .then(r => {
        setCampeonatos(r.data.data)
        if (!idcampeonato && r.data.data.length > 0) setIdcampeonato(r.data.data[0].idcampeonato)
      })
      .catch(() => setErro('Erro ao carregar campeonatos.'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Busca resultados da etapa atual e agenda atualizacao periodica
  useEffect(() => {
    if (!idcampeonato) return
    let cancelado = false

    function carregar() {
      api.get<ResultadosResponse>(`/publico/resultados?idcampeonato=${idcampeonato}`)
        .then(r => {
          if (cancelado) return
          setData(r.data)
          setUltimaAtualizacao(new Date())
          setErro(null)
        })
        .catch(() => { if (!cancelado) setErro('Erro ao carregar resultados.') })
    }

    carregar()
    const timer = setInterval(carregar, SEGUNDOS_ATUALIZAR_DADOS * 1000)
    return () => { cancelado = true; clearInterval(timer) }
  }, [idcampeonato])

  // Busca a classificacao geral (todas as etapas somadas) de cada modalidade —
  // reatualiza toda vez que os resultados da etapa mudam (mesmo ciclo de 20s)
  useEffect(() => {
    if (!data || !idcampeonato) return
    let cancelado = false

    Promise.all(
      data.modalidades.map(mod =>
        api.get<RankingGeralResponse>(`/publico/ranking-geral?idcampeonato=${idcampeonato}&idmodalidade=${mod.idmodalidade}`)
          .then(r => [mod.idmodalidade, r.data] as const)
          .catch(() => [mod.idmodalidade, null] as const),
      ),
    ).then(pares => {
      if (cancelado) return
      const map: Record<number, RankingGeralResponse> = {}
      for (const [idmodalidade, resp] of pares) if (resp) map[idmodalidade] = resp
      setRankingGeralMap(map)
    })

    return () => { cancelado = true }
  }, [data, idcampeonato])

  const slides = useMemo(
    () => [...montarSlidesEtapa(data, numeroEtapaAlvo), ...montarSlidesGeral(data, rankingGeralMap)],
    [data, numeroEtapaAlvo, rankingGeralMap],
  )

  // Avanca o slide automaticamente; volta ao inicio ao chegar no fim
  useEffect(() => {
    if (slides.length === 0) return
    setSlideIdx(i => (i >= slides.length ? 0 : i))
    const timer = setInterval(() => {
      setSlideIdx(i => (i + 1) % slides.length)
    }, SEGUNDOS_POR_SLIDE * 1000)
    return () => clearInterval(timer)
  }, [slides.length])

  const campNome = campeonatos.find(c => c.idcampeonato === idcampeonato)?.descricao ?? ''
  const slide = slides[slideIdx] ?? null

  const isPrecisao = slide?.grupo === 'Precisao'
  const isSilhueta = slide?.grupo === 'Silhueta'

  // Colunas com clamp(): reduzem em telas estreitas (celular/tablet) mas
  // mantem os tamanhos originais (pensados pra TV) em telas largas.
  const geralGridCols = slide?.tipo === 'geral'
    ? `clamp(24px,7vw,40px) minmax(80px,1fr) clamp(70px,13vw,130px) repeat(${slide.etapaColunas.length}, clamp(34px,8vw,60px)) clamp(44px,9vw,76px)`
    : undefined

  return (
    <div className="min-h-screen sm:h-screen w-full overflow-y-auto sm:overflow-hidden bg-[#0b0b0d] text-white flex flex-col">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top,rgba(200,161,60,0.08),transparent_55%)]" />

      {/* Cabecalho */}
      <header className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <img
            src="/sponsors/logo-aguia.jpeg"
            alt="Águia"
            className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover ring-2 ring-[#caa14d]/70 shrink-0"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <div className="min-w-0">
            <p className="text-xs sm:text-base font-black uppercase tracking-wide truncate">{campNome || 'Campeonato de Tiro'}</p>
            {slide && (
              <p className="text-[#caa14d] text-[9px] sm:text-[11px] font-semibold truncate">
                {slide.tipo === 'etapa' ? `${slide.etapaLabel} — Resultados Online` : 'Classificação Geral — Somatório de Todas as Etapas'}
              </p>
            )}
          </div>
        </div>
        <div className="text-left sm:text-right shrink-0">
          <p className="text-white/40 text-[8px] sm:text-[9px] uppercase tracking-wide">Atualizado</p>
          <p className="text-white/70 text-[9px] sm:text-[11px] font-mono tabular-nums">
            {ultimaAtualizacao ? ultimaAtualizacao.toLocaleTimeString('pt-BR') : '—'}
          </p>
        </div>
      </header>

      {/* Conteudo */}
      <main className="relative z-10 flex-1 flex flex-col px-1.5 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 min-h-0">
        {erro && (
          <div className="bg-red-950/50 border border-red-500/30 rounded-xl p-4 text-red-300 text-center">{erro}</div>
        )}

        {!erro && !slide && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <p className="text-xl sm:text-2xl md:text-3xl font-black text-white/80 mb-3">Aguardando o lançamento dos resultados...</p>
            <p className="text-white/40 text-sm sm:text-base">Os resultados aparecerão aqui assim que forem lançados no sistema.</p>
          </div>
        )}

        {slide && (
          <>
            {/* Titulo da modalidade */}
            <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1 sm:mb-2 shrink-0">
              <h1 className="text-sm sm:text-lg md:text-xl lg:text-2xl font-black uppercase tracking-tight text-white">{slide.modalidadeNome}</h1>
              <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
                {slide.tipo === 'geral' && (
                  <span className="text-[8px] sm:text-[10px] md:text-xs font-bold uppercase tracking-widest text-emerald-400 border border-emerald-400/40 rounded-full px-1.5 py-0.5 sm:px-2.5 sm:py-1">
                    Geral
                  </span>
                )}
                <span className="text-[8px] sm:text-[10px] md:text-xs font-bold uppercase tracking-widest text-[#caa14d] border border-[#caa14d]/40 rounded-full px-1.5 py-0.5 sm:px-2.5 sm:py-1">
                  {slide.grupo}
                </span>
              </div>
            </div>

            {/* Tabela — resultado da etapa atual */}
            {slide.tipo === 'etapa' && (
              <div className="min-h-0 bg-compumaq-pretoSuave/[0.03] border border-white/10 rounded-2xl overflow-hidden flex flex-col">
                <div className="grid grid-cols-12 bg-[#2a4f7f] text-white/90 text-[8px] sm:text-[10px] md:text-xs font-bold uppercase tracking-wide px-1.5 sm:px-3 py-0.5 sm:py-1 shrink-0">
                  <span className="col-span-1 text-center">Pos</span>
                  <span className="col-span-5">Atleta</span>
                  <span className="col-span-3">Clube</span>
                  <span className="col-span-3 text-right">{isPrecisao ? 'Pontos' : 'Total'}</span>
                </div>
                <div className="min-h-0 flex flex-col divide-y divide-white/5">
                  {slide.resultados.map((r, idx) => {
                    const cor = medalhaCor(r.posicao)
                    const valor = isPrecisao ? (r.total_pontos ?? r.total ?? 0) : (r.total ?? 0)
                    return (
                      <div
                        key={idx}
                        className="grid grid-cols-12 items-center px-1.5 sm:px-3 py-0.5 sm:py-1"
                        style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}
                      >
                        <span className="col-span-1 flex justify-center">
                          <span
                            className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] md:w-5 md:h-5 rounded-full flex items-center justify-center font-black text-[7px] sm:text-[9px] md:text-[10px] shrink-0"
                            style={cor ? { backgroundColor: cor, color: '#100f0c' } : { color: 'rgba(255,255,255,0.4)' }}
                          >
                            {r.posicao}
                          </span>
                        </span>
                        <span className="col-span-5 text-[9px] sm:text-xs md:text-sm font-bold truncate">{r.nome_atirador}</span>
                        <span className="col-span-3 text-white/50 text-[8px] sm:text-[10px] md:text-xs truncate">{r.clube_nome}</span>
                        <span className="col-span-3 text-right text-[9px] sm:text-xs md:text-base font-black text-emerald-400 tabular-nums pr-1 sm:pr-2">
                          {valor}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Tabela — classificacao geral (coluna por etapa + total) */}
            {slide.tipo === 'geral' && (
              <div className="min-h-0 bg-compumaq-pretoSuave/[0.03] border border-white/10 rounded-2xl overflow-hidden flex flex-col">
                <div className="min-h-0 overflow-x-auto flex flex-col">
                  <div
                    className="grid bg-[#2a4f7f] text-white/90 text-[8px] sm:text-[10px] md:text-xs font-bold uppercase tracking-wide px-1.5 sm:px-3 py-0.5 sm:py-1 shrink-0"
                    style={{ gridTemplateColumns: geralGridCols }}
                  >
                    <span className="text-center">Pos</span>
                    <span>Atleta</span>
                    <span>Clube</span>
                    {slide.etapaColunas.map(et => (
                      <span key={et.numero_etapa} className="text-right truncate">{et.label}</span>
                    ))}
                    <span className="text-right text-[#caa14d]">Total</span>
                  </div>
                  <div className="min-h-0 flex flex-col divide-y divide-white/5">
                    {slide.ranking.map((r, idx) => {
                      const cor = medalhaCor(r.posicao)
                      return (
                        <div
                          key={r.idatirador}
                          className="grid items-center px-1.5 sm:px-3 py-0.5 sm:py-1"
                          style={{ gridTemplateColumns: geralGridCols, backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}
                        >
                          <span className="flex justify-center">
                            <span
                              className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] md:w-5 md:h-5 rounded-full flex items-center justify-center font-black text-[7px] sm:text-[9px] md:text-[10px] shrink-0"
                              style={cor ? { backgroundColor: cor, color: '#100f0c' } : { color: 'rgba(255,255,255,0.4)' }}
                            >
                              {r.posicao}
                            </span>
                          </span>
                          <span className="text-[9px] sm:text-xs md:text-sm font-bold truncate">{r.nome_atirador}</span>
                          <span className="text-white/50 text-[8px] sm:text-[10px] md:text-xs truncate">{r.clube_nome}</span>
                          {slide.etapaColunas.map(et => (
                            <span key={et.numero_etapa} className="text-right text-[8px] sm:text-[10px] md:text-sm text-white/70 tabular-nums pr-1 sm:pr-2">
                              {r.etapas[et.numero_etapa] ?? '—'}
                            </span>
                          ))}
                          <span className="text-right text-[9px] sm:text-xs md:text-base font-black text-emerald-400 tabular-nums pr-1 sm:pr-2">
                            {r.total_geral}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Rodape: paginacao da modalidade + progresso geral */}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-1 sm:mt-2 shrink-0">
              <p className="text-white/30 text-[8px] sm:text-[10px]">
                {slide.totalPaginas > 1 ? `Página ${slide.pagina} de ${slide.totalPaginas}` : ''}
              </p>
              <div className="flex items-center gap-1 sm:gap-1.5 overflow-x-auto max-w-full">
                {slides.map((s, i) => (
                  <span
                    key={s.key}
                    className={`h-1 sm:h-1.5 rounded-full transition-all shrink-0 ${i === slideIdx ? 'w-5 sm:w-8 bg-[#caa14d]' : 'w-1 sm:w-1.5 bg-compumaq-pretoSuave/15'}`}
                  />
                ))}
              </div>
              <p className="text-white/30 text-[8px] sm:text-[10px]">{slideIdx + 1} / {slides.length}</p>
            </div>
          </>
        )}
      </main>
    </div>
  )
}
